import { useApi } from '@/api';
import {
  Banner,
  EntityName,
  KpiCard,
  KpiEntityName,
  Link,
  Page,
  PageSkeleton
} from '@/components';
import {
  CryptoIcon,
  Flex,
  Heading,
  LinkWrapper,
  NetworkIcon,
  Box
} from '@blockanalitica/ui';
import HistoricChart from './HistoricChart';

export default function MultichainPage() {
  const [data, error] = useApi({
    url: '/multichain/'
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <PageSkeleton />;
  }

  return (
    <Page
      title={
        <EntityName
          symbol1={<NetworkIcon name="solana" size="5" />}
          symbol2={<NetworkIcon name="base" size="5" />}
          symbol3={<NetworkIcon name="arbitrum" size="5" />}
          symbol4={<NetworkIcon name="optimism" size="5" />}
          symbol5={<NetworkIcon name="unichain" size="5" />}>
          <Heading size="1" marginLeft="-50px">
            <Flex gap="xs">Multichain</Flex>
          </Heading>
        </EntityName>
      }>
      <Flex gap={['m', 's']} flexDirection="column">
        <Banner>
          <Box paddingTop="xs" paddingBottom="m" maxWidth="85%">
            <Heading size="2">About the Sky Protocol Multichain</Heading>
            <br />
            Track USDS and sUSDS across leading chains — Solana, Base, Arbitrum,
            Optimism, and Unichain - beyond Ethereum mainnet, the home of the
            Sky Protocol.
          </Box>
        </Banner>
        <KpiCard
          title="Total Circulating Assets on other chains"
          field="circulating_assets"
          data={data}
          options={{
            currency: 'USD',
            prefixIcon: (
              <KpiEntityName
                symbol1={
                  <CryptoIcon
                    name="USDS"
                    address={data.usds_address}
                    size="3"
                  />
                }
                symbol2={<CryptoIcon name="sUSDS" size="3" />}></KpiEntityName>
            )
          }}
        />
      </Flex>

      <HistoricChart />
      <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
        <LinkWrapper
          to="/multichain/solana"
          // @ts-ignore
          as={Link}
          variant="kpiCardLinkWithBlendMode">
          <KpiCard
            title="Total on Solana"
            field="circulating_solana_balance"
            data={data}
            options={{
              currency: 'USD',
              linkArrow: true,
              notation: 'compact',
              prefixIcon: (
                <KpiEntityName
                  symbol1={
                    <CryptoIcon
                      name="USDS"
                      address={data.usds_address}
                      size="3"
                    />
                  }></KpiEntityName>
              )
            }}
          />
        </LinkWrapper>
        <LinkWrapper
          to="/multichain/base"
          // @ts-ignore
          as={Link}
          variant="kpiCardLinkWithBlendMode">
          <KpiCard
            title="Total on Base"
            field="circulating_base_balance"
            data={data}
            options={{
              currency: 'USD',
              linkArrow: true,
              notation: 'compact',
              prefixIcon: (
                <KpiEntityName
                  symbol1={
                    <CryptoIcon
                      name="USDS"
                      address={data.usds_address}
                      size="3"
                    />
                  }
                  symbol2={
                    <CryptoIcon name="sUSDS" size="3" />
                  }></KpiEntityName>
              )
            }}
          />
        </LinkWrapper>
        <LinkWrapper
          to="/multichain/arbitrum"
          // @ts-ignore
          as={Link}
          variant="kpiCardLinkWithBlendMode">
          <KpiCard
            title="Total on Arbitrum"
            field="circulating_arbitrum_balance"
            data={data}
            options={{
              currency: 'USD',
              linkArrow: true,
              notation: 'compact',
              prefixIcon: (
                <KpiEntityName
                  symbol1={
                    <CryptoIcon
                      name="USDS"
                      address={data.usds_address}
                      size="3"
                    />
                  }
                  symbol2={
                    <CryptoIcon name="sUSDS" size="3" />
                  }></KpiEntityName>
              )
            }}
          />
        </LinkWrapper>
      </Flex>
      <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
        {Number(data.circulating_optimism_balance) > 0 && (
          <LinkWrapper
            to="/multichain/optimism"
            // @ts-ignore
            as={Link}
            variant="kpiCardLinkWithBlendMode">
            <KpiCard
              title="Total on Optimism"
              field="circulating_optimism_balance"
              data={data}
              options={{
                currency: 'USD',
                linkArrow: true,
                notation: 'compact',
                prefixIcon: (
                  <KpiEntityName
                    symbol1={
                      <CryptoIcon
                        name="USDS"
                        address={data.usds_address}
                        size="3"
                      />
                    }
                    symbol2={
                      <CryptoIcon name="sUSDS" size="3" />
                    }></KpiEntityName>
                )
              }}
            />
          </LinkWrapper>
        )}
        {Number(data.circulating_unichain_balance) > 0 && (
          <LinkWrapper
            to="/multichain/unichain"
            // @ts-ignore
            as={Link}
            variant="kpiCardLinkWithBlendMode">
            <KpiCard
              title="Total on Unichain"
              field="circulating_unichain_balance"
              data={data}
              options={{
                currency: 'USD',
                linkArrow: true,
                notation: 'compact',
                prefixIcon: (
                  <KpiEntityName
                    symbol1={
                      <CryptoIcon
                        name="USDS"
                        address={data.usds_address}
                        size="3"
                      />
                    }
                    symbol2={
                      <CryptoIcon name="sUSDS" size="3" />
                    }></KpiEntityName>
                )
              }}
            />
          </LinkWrapper>
        )}
      </Flex>
    </Page>
  );
}
